import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.success) {
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data));
    }
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (email, resetToken, newPassword) => {
    const response = await api.post('/auth/reset-password', {
      email,
      resetToken,
      newPassword
    });
    return response.data;
  }
};

// Patient services
export const patientService = {
  getAll: async (params = {}) => {
    const response = await api.get('/patients', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/patients/${id}`);
    return response.data;
  },

  create: async (patientData) => {
    const response = await api.post('/patients', patientData);
    return response.data;
  },

  update: async (id, patientData) => {
    const response = await api.put(`/patients/${id}`, patientData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/patients/${id}`);
    return response.data;
  },

  searchByDocument: async (documentNumber) => {
    const response = await api.get(`/patients/document/${documentNumber}`);
    return response.data;
  }
};

// Doctor services
export const doctorService = {
  getAll: async (params = {}) => {
    const response = await api.get('/doctors', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/doctors/${id}`);
    return response.data;
  },

  create: async (doctorData) => {
    const config = doctorData instanceof FormData ? {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    } : {};
    const response = await api.post('/doctors', doctorData, config);
    return response.data;
  },

  update: async (id, doctorData) => {
    const config = doctorData instanceof FormData ? {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    } : {};
    const response = await api.put(`/doctors/${id}`, doctorData, config);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/doctors/${id}`);
    return response.data;
  },

  toggleActive: async (id) => {
    const response = await api.patch(`/doctors/${id}/toggle-active`);
    return response.data;
  },

  updateSchedule: async (id, schedule) => {
    const response = await api.put(`/doctors/${id}/schedule`, { schedule });
    return response.data;
  },

  getBySpecialty: async (specialty) => {
    const response = await api.get(`/doctors/specialty/${specialty}`);
    return response.data;
  },

  getAvailableByDay: async (day) => {
    const response = await api.get(`/doctors/available/${day}`);
    return response.data;
  },

  getSpecialties: async () => {
    const response = await api.get('/doctors/specialties/list');
    return response.data;
  }
};

// Appointment services (for future implementation)
export const appointmentService = {
  create: async (appointmentData) => {
    const response = await api.post('/appointments', appointmentData);
    return response.data;
  },

  getAll: async (params = {}) => {
    const response = await api.get('/appointments', { params });
    return response.data;
  }
};

// Medical History services
export const medicalHistoryService = {
  create: async (historyData) => {
    const response = await api.post('/medical-history', historyData);
    return response.data;
  },

  getAll: async (params = {}) => {
    const response = await api.get('/medical-history', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/medical-history/${id}`);
    return response.data;
  },

  getByPatient: async (patientId) => {
    const response = await api.get(`/medical-history/patient/${patientId}`);
    return response.data;
  },

  update: async (id, historyData) => {
    const response = await api.put(`/medical-history/${id}`, historyData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/medical-history/${id}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/medical-history/stats');
    return response.data;
  }
};

// Catalog services
export const catalogService = {
  getAll: async () => {
    const response = await api.get('/catalogs');
    return response.data;
  },

  generateQR: async (filename) => {
    const response = await api.get(`/catalogs/qr/${encodeURIComponent(filename)}`);
    return response.data;
  }
};

export default api;
